import React, { useEffect } from 'react';
import { Icon, InfoMessage, Message, PageSubtitle, PageTitle } from '@fasstech/middle-design-system';
import ButtonContainer from '@@viewCommonComponents/forms/ButtonContainer.jsx';
import { FormLayout, PageLayout } from '@@viewCommonComponents/forms/Layouts.jsx';
import { FormProvider, useForm } from 'react-hook-form';
import useSouscriptionInformationsForm from './hooks/useSouscriptionInformationsForm.js';
import { and, isEmpty, not, propOr } from 'ramda';
import { formatZipCode, isRequiredBooleanTrue, isRequiredInput, isRequiredSelect, landlinePhoneNumberRegex, mobilePhoneNumberRegex, zipCodeRules } from '@@viewHelpers/validations';
import { ControlledInput, ControlledPhoneInput, ControlledSelect } from '@@viewCommonComponents/controlled/index.js';
import useFetchCitiesQuery from './hooks/useFetchCitiesQuery.js';
import hasExtraUnrequiredFields from './extra/hasExtraUnrequiredFields.js';
import useTelEmailValidation from '@@viewCommonHooks/useTelEmailValidation.js';
import ControlledCheckbox from '@@viewCommonComponents/controlled/ControlledCheckbox.jsx';
import * as EmailValidator from 'email-validator';
import useMailMobileLandlineCrossValidation from '../../_common/client/hooks/useMobileLandlineCrossValidation.mjs';
import getExtraAdherentConsentementTelText
  from '../../simulationCustomerGrantees/client/extra/getExtraAdherentConsentementTelText.js';
import { isNotNilOrEmpty } from '@@serverHelpers';

const SouscriptionInformationsForm = ({
  getMiddleText,
  getMiddleBoolean,
  phoneIndicatives = [],
  postalCodeFranceMetro,
  postalCodeDROM,
  data
}) => {
  const {
    onPrevious,
    onSubmit,
    formatCustomer
  } =
    useSouscriptionInformationsForm();

  const form = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: formatCustomer(data)
  });

  const { isValid } = form.formState;

  useEffect(() => {
    const timeout = setTimeout(() => {
      form.trigger();
    }, 0);
    return () => clearTimeout(timeout);
  }, []);

  const emailConsent = form.watch('customer.GDPR.email');
  const phoneConsent = form.watch('customer.GDPR.phone');
  useEffect(() => {
    form.trigger('customer.GDPR.email');
    form.trigger('customer.GDPR.phone');
  }, [emailConsent, phoneConsent]);

  const mobileNumber = form.watch('customer.phones.0.number');
  const landlineNumber = form.watch('customer.phones.1.number');
  const aPhoneNumberHasAValueAndIsValid =
    (!isEmpty(mobileNumber) && mobilePhoneNumberRegex.test(mobileNumber)) ||
    (!isEmpty(landlineNumber) && landlinePhoneNumberRegex.test(landlineNumber));
  const email = form.watch('customer.emails.0.value');
  const emailHasAValueAndIsValid =
    !isEmpty(email) && EmailValidator.validate(email);

  const postalCode = form.watch('customer.addresses.0.zipCode');
  const shouldSkipFetchCities = !getMiddleBoolean('ADHERENT_VILLE_EST_NECESSAIRE');

  const { fetchCities: cities } = useFetchCitiesQuery({
    variables: {
      postalCode
    },
    skip: shouldSkipFetchCities
  });

  const {
    telEmailValidationHasError,
    telEmailValidationErrorMessage
  } =
    useTelEmailValidation(form, getMiddleBoolean);

  const getMailMobileLandlineInputRules = useMailMobileLandlineCrossValidation(
    form,
    getMiddleBoolean
  );

  const displayCommercialConsent =
    getMiddleBoolean('ADHERENT_CONSENTEMENT_COMMERCIAL_EST_NECESSAIRE') &&
    !getMiddleBoolean(
      'ADHERENT_CONSENTEMENT_COMMERCIAL_EST_DEMANDE_DONNEES_CONTRAT'
    ) &&
    getMiddleBoolean(
      'ADHERENT_CONSENTEMENT_COMMERCIAL_EST_DEMANDE_DONNEES_SOUSCRIPTION'
    );

  return (
    <FormProvider {...form}>
      <form className={'w-full'} onSubmit={form.handleSubmit(onSubmit)}>
        <PageLayout>
          <Icon name={'houseRounded'} size={70}/>
          <PageTitle>
            {getMiddleText('DONNEES_SOUSCRIPTION_COORD_VIEW_TITLE')}
          </PageTitle>
          <PageSubtitle>
            {getMiddleText('DONNEES_SOUSCRIPTION_COORD_VIEW_DESCRIPTION')}
          </PageSubtitle>

          <FormLayout>
            <ControlledInput
              name="customer.addresses.0.fields.0"
              label={getMiddleText('ADHERENT_ADRESSE_LABEL_CHAMP1')}
              form={form}
              rules={isRequiredInput(
                getMiddleBoolean('ADHERENT_ADRESSE_CHAMP1_EST_OBLIGATOIRE')
              )}
              disabled={and(
                getMiddleBoolean('ADHERENT_ADRESSE_CHAMP1_EST_OBLIGATOIRE'),
                getMiddleBoolean(
                  'ADHERENT_ADRESSE_EST_DEMANDE_DONNEES_CONTRAT'
                )
              )}
              data-testid="ADHERENT_ADRESSE_CHAMP1"
              isDisplayed={
                getMiddleBoolean('ADHERENT_CODE_POSTAL_EST_NECESSAIRE') &&
                getMiddleBoolean('ADHERENT_ADRESSE_EST_NECESSAIRE') &&
                getMiddleBoolean(
                  'ADHERENT_ADRESSE_EST_DEMANDE_DONNEES_SOUSCRIPTION'
                )
              }
            />
            <ControlledInput
              name="customer.addresses.0.fields.1"
              label={getMiddleText('ADHERENT_ADRESSE_LABEL_CHAMP2')}
              form={form}
              rules={isRequiredInput(
                getMiddleBoolean('ADHERENT_ADRESSE_CHAMP2_EST_OBLIGATOIRE')
              )}
              disabled={and(
                getMiddleBoolean('ADHERENT_ADRESSE_CHAMP2_EST_OBLIGATOIRE'),
                getMiddleBoolean(
                  'ADHERENT_ADRESSE_EST_DEMANDE_DONNEES_CONTRAT'
                )
              )}
              data-testid="ADHERENT_ADRESSE_CHAMP2"
              isDisplayed={
                getMiddleBoolean('ADHERENT_CODE_POSTAL_EST_NECESSAIRE') &&
                getMiddleBoolean('ADHERENT_ADRESSE_EST_NECESSAIRE') &&
                getMiddleBoolean(
                  'ADHERENT_ADRESSE_EST_DEMANDE_DONNEES_SOUSCRIPTION'
                )
              }
            />
            <ControlledInput
              name="customer.addresses.0.fields.2"
              label={getMiddleText('ADHERENT_ADRESSE_LABEL_CHAMP3')}
              form={form}
              containerClassName="col-span-2"
              rules={isRequiredInput(
                getMiddleBoolean('ADHERENT_ADRESSE_CHAMP3_EST_OBLIGATOIRE')
              )}
              disabled={and(
                getMiddleBoolean('ADHERENT_ADRESSE_CHAMP3_EST_OBLIGATOIRE'),
                getMiddleBoolean(
                  'ADHERENT_ADRESSE_EST_DEMANDE_DONNEES_CONTRAT'
                )
              )}
              data-testid="ADHERENT_ADRESSE_CHAMP3"
              isDisplayed={
                getMiddleBoolean('ADHERENT_CODE_POSTAL_EST_NECESSAIRE') &&
                getMiddleBoolean('ADHERENT_ADRESSE_EST_NECESSAIRE') &&
                getMiddleBoolean(
                  'ADHERENT_ADRESSE_EST_DEMANDE_DONNEES_SOUSCRIPTION'
                )
              }
            />
            {getMiddleBoolean('ADHERENT_CODE_POSTAL_EST_NECESSAIRE') &&
              (getMiddleBoolean(
                'ADHERENT_CODE_POSTAL_EST_DEMANDE_DONNEES_CONTRAT'
              ) ||
                getMiddleBoolean(
                  'ADHERENT_CODE_POSTAL_EST_DEMANDE_DONNEES_SOUSCRIPTION'
                )) && (
              <ControlledInput
                name="customer.addresses.0.zipCode"
                label={getMiddleText('ADHERENT_CODE_POSTAL_LABEL')}
                form={form}
                rules={zipCodeRules(
                  getMiddleBoolean('ADHERENT_CODE_POSTAL_EST_OBLIGATOIRE'),
                  postalCodeFranceMetro,
                  postalCodeDROM
                )}
                formatValue={formatZipCode}
                disabled={and(
                  getMiddleBoolean('ADHERENT_CODE_POSTAL_EST_OBLIGATOIRE'),
                  getMiddleBoolean(
                    'ADHERENT_CODE_POSTAL_EST_DEMANDE_DONNEES_CONTRAT'
                  )
                )}
                data-testid="ADHERENT_CODE_POSTAL"
              />
            )}
            <ControlledSelect
              name="customer.addresses.0.city"
              label={getMiddleText('ADHERENT_VILLE_LABEL')}
              form={form}
              options={propOr([], 'cities', cities)}
              rules={isRequiredSelect(
                propOr([], 'cities', cities),
                getMiddleBoolean('ADHERENT_VILLE_EST_OBLIGATOIRE')
              )}
              disabled={and(
                getMiddleBoolean('ADHERENT_VILLE_EST_OBLIGATOIRE'),
                getMiddleBoolean('ADHERENT_VILLE_EST_DEMANDE_DONNEES_CONTRAT')
              )}
              data-testid="ADHERENT_VILLE"
              isDisplayed={
                getMiddleBoolean('ADHERENT_CODE_POSTAL_EST_NECESSAIRE') &&
                getMiddleBoolean('ADHERENT_VILLE_EST_NECESSAIRE') &&
                (getMiddleBoolean(
                  'ADHERENT_VILLE_EST_DEMANDE_DONNEES_CONTRAT'
                ) ||
                  getMiddleBoolean(
                    'ADHERENT_VILLE_EST_DEMANDE_DONNEES_SOUSCRIPTION'
                  ))
              }
            />
            <ControlledPhoneInput
              name={'customer.phones.0'}
              label={getMiddleText('ADHERENT_TEL_MOBILE_LABEL')}
              form={form}
              rules={getMailMobileLandlineInputRules('mobile')}
              isInternational
              options={phoneIndicatives}
              disabled={
                (getMiddleBoolean('ADHERENT_TEL_MOBILE_EST_OBLIGATOIRE') ||
                  getMiddleBoolean('ADHERENT_TEL_OU_FIXE_EST_OBLIGATOIRE') ||
                  getMiddleBoolean('ADHERENT_TEL_OU_EMAIL_EST_OBLIGATOIRE')) &&
                !isEmpty(mobileNumber) &&
                getMiddleBoolean(
                  'ADHERENT_TEL_MOBILE_EST_DEMANDE_DONNEES_CONTRAT'
                )
              }
              data-testid="ADHERENT_TEL_MOBILE"
              isDisplayed={
                getMiddleBoolean('ADHERENT_TEL_MOBILE_EST_NECESSAIRE') &&
                (getMiddleBoolean(
                  'ADHERENT_TEL_MOBILE_EST_DEMANDE_DONNEES_CONTRAT'
                ) ||
                  getMiddleBoolean(
                    'ADHERENT_TEL_MOBILE_EST_DEMANDE_DONNEES_SOUSCRIPTION'
                  ))
              }
            />
            <InfoMessage
              iconName={'lightbulb'}
              isDisplayed={
                getMiddleBoolean('ADHERENT_TEL_MOBILE_EST_NECESSAIRE') &&
                (getMiddleBoolean(
                  'ADHERENT_TEL_MOBILE_EST_DEMANDE_DONNEES_CONTRAT'
                ) ||
                  getMiddleBoolean(
                    'ADHERENT_TEL_MOBILE_EST_DEMANDE_DONNEES_SOUSCRIPTION'
                  )) &&
                getMiddleBoolean('ADHERENT_TEL_MOBILE_NOTE_EST_AFFICHEE')
              }
              variant="info"
              className={'w-full'}
            >
              {getMiddleText('ADHERENT_TEL_MOBILE_NOTE_LABEL')}
            </InfoMessage>
            <ControlledPhoneInput
              name={'customer.phones.1'}
              isMobile={false}
              label={getMiddleText('ADHERENT_TEL_FIXE_LABEL')}
              form={form}
              rules={getMailMobileLandlineInputRules('landline')}
              disabled={
                (getMiddleBoolean('ADHERENT_TEL_FIXE_EST_OBLIGATOIRE') ||
                  getMiddleBoolean('ADHERENT_TEL_OU_FIXE_EST_OBLIGATOIRE')) &&
                !isEmpty(landlineNumber) &&
                getMiddleBoolean(
                  'ADHERENT_TEL_FIXE_EST_DEMANDE_DONNEES_CONTRAT'
                )
              }
              data-testid="ADHERENT_TEL_FIXE"
              messageClassName="!mb-0"
              isDisplayed={
                getMiddleBoolean('ADHERENT_TEL_FIXE_EST_NECESSAIRE') &&
                (getMiddleBoolean(
                  'ADHERENT_TEL_FIXE_EST_DEMANDE_DONNEES_CONTRAT'
                ) ||
                  getMiddleBoolean(
                    'ADHERENT_TEL_FIXE_EST_DEMANDE_DONNEES_SOUSCRIPTION'
                  ))
              }
            />
            <ControlledCheckbox
              isDisplayed={
                aPhoneNumberHasAValueAndIsValid &&
                getMiddleBoolean('ADHERENT_CONSENTEMENT_TEL_EST_NECESSAIRE') &&
                (getMiddleBoolean('ADHERENT_TEL_MOBILE_EST_NECESSAIRE') ||
                  getMiddleBoolean('ADHERENT_TEL_MOBILE_EST_NECESSAIRE')) &&
                (getMiddleBoolean(
                  'ADHERENT_TEL_MOBILE_EST_DEMANDE_DONNEES_CONTRAT'
                ) ||
                  getMiddleBoolean(
                    'ADHERENT_TEL_FIXE_EST_DEMANDE_DONNEES_CONTRAT'
                  ) ||
                  getMiddleBoolean(
                    'ADHERENT_TEL_MOBILE_EST_DEMANDE_DONNEES_SOUSCRIPTION'
                  ) ||
                  getMiddleBoolean(
                    'ADHERENT_TEL_FIXE_EST_DEMANDE_DONNEES_SOUSCRIPTION'
                  ))
              }
              disabled={
                getMiddleBoolean(
                  'ADHERENT_TEL_MOBILE_EST_DEMANDE_DONNEES_CONTRAT'
                ) ||
                getMiddleBoolean(
                  'ADHERENT_TEL_FIXE_EST_DEMANDE_DONNEES_CONTRAT'
                )
              }
              containerClassName="mt-[-20px] mb-4"
              labelClassNames="pl-4"
              name={'customer.GDPR.phone'}
              label={getExtraAdherentConsentementTelText(
                getMiddleText('ADHERENT_CONSENTEMENT_TEL_TEXT')
              )}
              form={form}
              rules={isRequiredBooleanTrue(
                (getMiddleBoolean(
                  'ADHERENT_CONSENTEMENT_TEL_EST_OBLIGATOIRE'
                ) &&
                  !getMiddleBoolean(
                    'ADHERENT_CONSENTEMENT_TEL_OU_EMAIL_EST_OBLIGATOIRE'
                  )) ||
                (getMiddleBoolean(
                  'ADHERENT_CONSENTEMENT_TEL_OU_EMAIL_EST_OBLIGATOIRE'
                ) &&
                  !emailConsent)
              )}
            />
            <ControlledInput
              name="customer.emails.0.value"
              label={getMiddleText('ADHERENT_EMAIL_LABEL')}
              form={form}
              displayErrorMessage={true}
              rules={getMailMobileLandlineInputRules('email')}
              disabled={
                (getMiddleBoolean('ADHERENT_EMAIL_EST_OBLIGATOIRE') ||
                  getMiddleBoolean('ADHERENT_TEL_OU_EMAIL_EST_OBLIGATOIRE')) &&
                !isEmpty(email) &&
                getMiddleBoolean('ADHERENT_EMAIL_EST_DEMANDE_DONNEES_CONTRAT')
              }
              data-testid="ADHERENT_EMAIL"
              messageClassName="!mb-0"
              isDisplayed={
                getMiddleBoolean('ADHERENT_EMAIL_EST_NECESSAIRE') &&
                (getMiddleBoolean(
                  'ADHERENT_EMAIL_EST_DEMANDE_DONNEES_CONTRAT'
                ) ||
                  getMiddleBoolean(
                    'ADHERENT_EMAIL_EST_DEMANDE_DONNEES_SOUSCRIPTION'
                  ))
              }
            />
            <InfoMessage
              iconName={'lightbulb'}
              isDisplayed={
                getMiddleBoolean('ADHERENT_EMAIL_EST_NECESSAIRE') &&
                (getMiddleBoolean(
                  'ADHERENT_EMAIL_EST_DEMANDE_DONNEES_CONTRAT'
                ) ||
                  getMiddleBoolean(
                    'ADHERENT_EMAIL_EST_DEMANDE_DONNEES_SOUSCRIPTION'
                  )) &&
                getMiddleBoolean('ADHERENT_EMAIL_NOTE_EST_AFFICHEE')
              }
              variant="info"
              className={'w-full'}
            >
              {getMiddleText('ADHERENT_EMAIL_NOTE_LABEL')}
            </InfoMessage>
            <ControlledCheckbox
              isDisplayed={
                emailHasAValueAndIsValid &&
                getMiddleBoolean(
                  'ADHERENT_CONSENTEMENT_EMAIL_EST_NECESSAIRE'
                ) &&
                getMiddleBoolean('ADHERENT_EMAIL_EST_NECESSAIRE') &&
                (getMiddleBoolean(
                  'ADHERENT_EMAIL_EST_DEMANDE_DONNEES_CONTRAT'
                ) ||
                  getMiddleBoolean(
                    'ADHERENT_EMAIL_EST_DEMANDE_DONNEES_SOUSCRIPTION'
                  ))
              }
              disabled={getMiddleBoolean(
                'ADHERENT_EMAIL_EST_DEMANDE_DONNEES_CONTRAT'
              )}
              containerClassName="mt-[-20px] mb-4"
              labelClassNames="pl-4"
              name={'customer.GDPR.email'}
              label={getMiddleText('ADHERENT_CONSENTEMENT_EMAIL_TEXT')}
              form={form}
              rules={isRequiredBooleanTrue(
                (getMiddleBoolean(
                  'ADHERENT_CONSENTEMENT_EMAIL_EST_OBLIGATOIRE'
                ) &&
                  !getMiddleBoolean(
                    'ADHERENT_CONSENTEMENT_TEL_OU_EMAIL_EST_OBLIGATOIRE'
                  )) ||
                (getMiddleBoolean(
                  'ADHERENT_CONSENTEMENT_TEL_OU_EMAIL_EST_OBLIGATOIRE'
                ) &&
                  !phoneConsent)
              )}
            />
            <Message
              className="w-full"
              variant={'error'}
              label={telEmailValidationErrorMessage}
            />
            <ControlledCheckbox
              isDisplayed={
                displayCommercialConsent &&
                isNotNilOrEmpty(
                  getMiddleText('ADHERENT_CONSENTEMENT_COMMERCIAL_MENTION1')
                )
              }
              labelClassNames="pl-4"
              name={'customer.GDPR.company'}
              label={getMiddleText(
                'ADHERENT_CONSENTEMENT_COMMERCIAL_MENTION1',
                { companyName: getMiddleText('GENERAL_CLIENT_NOM') }
              )}
              form={form}
            />
            <ControlledCheckbox
              isDisplayed={
                displayCommercialConsent &&
                isNotNilOrEmpty(
                  getMiddleText('ADHERENT_CONSENTEMENT_COMMERCIAL_MENTION2')
                )
              }
              labelClassNames="pl-4"
              name={'customer.GDPR.partners'}
              label={getMiddleText(
                'ADHERENT_CONSENTEMENT_COMMERCIAL_MENTION2',
                { companyName: getMiddleText('GENERAL_CLIENT_NOM') }
              )}
              form={form}
            />
          </FormLayout>
        </PageLayout>

        <ButtonContainer
          $onPreviousButtonClick={onPrevious}
          nextButtonLabel={getMiddleText(
            'GENERAL_COMMON_TEXTS_NEXT_BUTTON_LABEL'
          )}
          displayResumeButton
          nextButtonIsDisabled={
            not(isValid) ||
            !hasExtraUnrequiredFields(form) ||
            telEmailValidationHasError
          }
          isFormError={
            not(isValid) ||
            !hasExtraUnrequiredFields(form) ||
            telEmailValidationHasError
          }
          getMiddleText={getMiddleText}
        />
      </form>
    </FormProvider>
  );
};

export default SouscriptionInformationsForm;
